'use client';

import { useVirtualizer } from '@tanstack/react-virtual';
import { useTranslations } from 'next-intl';
import { useRef, useMemo, useEffect } from 'react';

import { UiKitLoaderBase } from '@/fo/ui/kit/loader/base';
import { UiKitSelectBase } from '@/fo/ui/kit/select/base';

import { PointFeature, PointType } from '../../types';
import { PickupPointInfo } from '../PickupPointInfo/PickupPointInfo';

import { styles } from './PickupPointSelector.styles';

export interface PickupPointSelectorProps {
  pointsList: PointFeature[];
  pointTypes: PointType[];
  selectedPointType: PointType | null;
  onSelectPoint: (point: PointFeature) => void;
  activePointId: string | null;
  onPointTypeChange: (pointType: PointType | null) => void;
  onPointConfirm: (pointId: string) => void;
  pointSelectorTitle?: string;
  isHasPointTypeSelector?: boolean;
  size?: 'small' | 'default';
  hideConfirmButton?: boolean;
  loading?: boolean;
  isFreeShipping?: boolean;
}

export function PickupPointSelector({
  onSelectPoint,
  activePointId,
  pointsList,
  pointTypes,
  selectedPointType,
  onPointTypeChange,
  onPointConfirm,
  pointSelectorTitle,
  isHasPointTypeSelector = true,
  size = 'default',
  hideConfirmButton = false,
  loading = false,
  isFreeShipping
}: PickupPointSelectorProps) {
  const slots = styles({ size });

  const t = useTranslations();

  const pointListRef = useRef<HTMLDivElement>(null);

  const options = useMemo(
    () =>
      pointTypes.map((type) => ({
        label: type,
        value: type
      })),
    [pointTypes]
  );

  const virtualizer = useVirtualizer({
    count: pointsList.length,
    getItemKey: (index) => pointsList[index].id,
    estimateSize: () => 80,
    getScrollElement: () => pointListRef.current
  });

  const virtualizerItems = virtualizer.getVirtualItems();

  useEffect(() => {
    virtualizer.scrollToIndex(
      pointsList.findIndex((point) => point.id === activePointId),
      { align: 'start' }
    );
  }, [activePointId, pointsList, virtualizer]);

  const selectedPoint =
    options.find((option) => option.value === selectedPointType) || null;

  return (
    <div className={slots.mobileContainer()}>
      <div className={slots.base()}>
        <h2 className={slots.title({ withMargin: !isHasPointTypeSelector })}>
          {pointSelectorTitle || t('pickupPoints')}
        </h2>
        {isHasPointTypeSelector && (
          <div className={slots.pointTypeSelector()}>
            <UiKitSelectBase
              selected={selectedPoint}
              onChange={(option) => {
                onPointTypeChange((option?.value as PointType) || null);
              }}
              placeholder={t('pickupServices.title')}
              options={options}
            />
          </div>
        )}
        <div
          ref={pointListRef}
          className={slots.pointsScrollWrapper()}
        >
          <div
            className={slots.pointsScrollInner()}
            style={{
              height: virtualizer.getTotalSize()
            }}
          >
            <div
              className={slots.pointList()}
              style={{
                transform: `translateY(${virtualizerItems[0]?.start ?? 0}px)`
              }}
            >
              {virtualizerItems.map((virtualItem) => {
                const point = pointsList[virtualItem.index];
                const isActive = point.id === activePointId;

                return (
                  <div
                    key={virtualItem.key}
                    data-index={virtualItem.index}
                    ref={virtualizer.measureElement}
                    className={slots.pointItemContainer()}
                  >
                    <div
                      role="button"
                      className={slots.pointItem({ isActive })}
                      onClick={() => onSelectPoint(point)}
                    >
                      <PickupPointInfo
                        point={point}
                        activePointId={activePointId}
                        onPointConfirm={onPointConfirm}
                        hideConfirmButton={hideConfirmButton}
                        isFreeShipping={isFreeShipping}
                      />
                    </div>
                  </div>
                );
              })}
              {virtualizerItems.length === 0 && !loading && (
                <div className={slots.emptyState()}>{t('noPointsFound')}</div>
              )}
            </div>
          </div>
        </div>
        <UiKitLoaderBase show={loading} />
      </div>
    </div>
  );
}
